import React, { useEffect, useState } from "react";

import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Slider from "@mui/material/Slider";

import {
  VIDEO_LENGTH_MAX,
  VIDEO_LENGTH_MIN,
  VIDEO_LENGTH_STEP,
} from "../../../common/components/inputs/ptz/constants";
import { VideoStatusEnum } from "../../../common/models/enums";
import { getTimeRange } from "../../../common/utils/getTimeRange";
import { ISecurityEventsTableRow } from "../../data-hub/components/models/common/events.models";
import { useRetrieveVideo } from "../hooks/useRetrieveVideo";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { getRecordingS3Key } from "../models/security/hooks/useGetSecurityEventsTableColumns";

interface RetrieveVideoModalProps {
  open: boolean;
  onClose: () => void;
  nodeId?: string;
  deviceId: string;
  timestamp: string;
  s3Key: string;
  row: ISecurityEventsTableRow;
  startRetrieveVideo: () => void;
}

const RetrieveVideoModal: React.FC<RetrieveVideoModalProps> = ({
  open,
  onClose,
  nodeId,
  deviceId,
  timestamp,
  s3Key,
  row,
  startRetrieveVideo,
}) => {
  const handleClose = () => {
    onClose();
  };

  const [videoLength, setVideoLength] = useState(10);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  useEffect(() => {
    if (timestamp) {
      const { startDate, endDate } = getTimeRange(timestamp, videoLength);

      setStartTime(startDate);

      setEndTime(endDate);
    }
  }, [timestamp, videoLength]);

  const { createVideo } = useRetrieveVideo();

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setVideoLength(newValue as number);
  };

  const handleSliderInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value === "" ? 0 : Number(event.target.value);

    setVideoLength(newValue);
  };

  const handleBlur = () => {
    if (videoLength < 5) {
      setVideoLength(5);
    } else if (videoLength > 20) {
      setVideoLength(20);
    }
  };

  const handleSubmit = async () => {
    if (nodeId && deviceId) {
      try {
        // Create timestream dimensions and timestamp for the video status update
        const timestreamDimensions = {
          locationId: row.location,
          inWindow: row.inWindow,
          customerId: row.customerId,
          cameraId: row.cameraId,
        };

        const timestreamTimestamp = row.timestreamTimestamp;

        const timestreamRow = {
          timestreamDimensions,
          timestreamTimestamp,
          timestreamMeasureValues: [
            { Name: "model", Value: "safetysecurity", Type: "VARCHAR" },
            { Name: "keyframe", Value: row.keyFrame, Type: "VARCHAR" },
            { Name: "mediaOutput", Value: row.mediaOutput, Type: "VARCHAR" },
            {
              Name: "vid_status",
              Value: VideoStatusEnum.RETRIEVING,
              Type: "VARCHAR",
            },
            { Name: "people_c", Value: row.peopleCount, Type: "BIGINT" },
            { Name: "vehicle_c", Value: row.vehicleCount, Type: "BIGINT" },
            { Name: "people_delta", Value: row.peopleDelta, Type: "BIGINT" },
            { Name: "vehicle_delta", Value: row.vehicleDelta, Type: "BIGINT" },
            { Name: "is_del", Value: String(row.isDeleted), Type: "BOOLEAN" },
            {
              Name: "node_id",
              Value: row.nodeId,
              Type: "VARCHAR",
            },
            {
              Name: "process_time",
              Value: row.processTime,
              Type: "BIGINT",
            },
          ],
        };

        startRetrieveVideo();

        successNotification("Starting video retrieval");

        handleClose();

        const recordingS3Key = getRecordingS3Key(s3Key);

        await createVideo({
          deviceIdClean: deviceId,
          nodeIdClean: nodeId,
          s3Key: recordingS3Key,
          startTime,
          endTime,
          videoLength,
          timestreamRow: JSON.stringify(timestreamRow),
          ddbRow: null,
        });
      } catch (error) {
        console.error("Failed to retrieve video:", error);

        errorNotification("Error retrieving video");
      }
    } else {
      console.error("Missing nodeId or deviceId");

      errorNotification("Error retrieving video");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h4">Retrieve Video</Typography>
        <Button
          onClick={handleClose}
          sx={{
            minWidth: "auto",
            p: 0.5,
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h5">Video length</Typography>

          <Typography variant="body1" color="text.secondary">
            Select the total video length to be generated around the event. The
            system will split the duration equally before and after the event.
          </Typography>
          <Box sx={{ width: "100%" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Typography variant="body1">5m</Typography>
              </Grid>
              <Grid item xs>
                <Slider
                  value={typeof videoLength === "number" ? videoLength : 0}
                  onChange={handleSliderChange}
                  valueLabelDisplay="on"
                  min={VIDEO_LENGTH_MIN}
                  max={VIDEO_LENGTH_MAX}
                  step={VIDEO_LENGTH_STEP}
                />
              </Grid>
              <Grid item>
                <Typography variant="body1">20m</Typography>
              </Grid>
              <Grid item>
                <TextField
                  value={videoLength}
                  size="small"
                  onChange={handleSliderInputChange}
                  onBlur={handleBlur}
                  type="number"
                  inputProps={{
                    min: VIDEO_LENGTH_MIN,
                    max: VIDEO_LENGTH_MAX,
                    type: "number",
                  }}
                  sx={{ width: 60 }}
                  InputProps={{
                    endAdornment: (
                      <Typography variant="body2" color="text.secondary">
                        m
                      </Typography>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleSubmit} variant="contained" fullWidth>
          Retrieve Video
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RetrieveVideoModal;
